// src/hooks/useSEO.ts
// Custom SEO Hook voor React 19 - geen externe dependencies nodig!
// Manipuleert document head direct via vanilla JS

import { useEffect } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOConfig {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage?: string;
  examType?: string;
  ogType?: 'website' | 'article';
  articleMeta?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
  faqs?: FAQItem[];
  courseInfo?: {
    name: string;
    description: string;
    duration: string;
    numberOfQuestions: number;
    passingScore: string;
    price: string;
  };
}

const BASE_URL = 'https://wft-study-buddy.nl';
const SITE_NAME = 'WFT Study Buddy';

export const useSEO = (config: SEOConfig) => {
  useEffect(() => {
    const {
      title,
      description,
      canonicalUrl,
      ogImage = `${BASE_URL}/og-image.webp`,
      examType,
      ogType = 'website',
      articleMeta,
      faqs = [],
      courseInfo
    } = config;

    const fullUrl = `${BASE_URL}${canonicalUrl}`;

    // Helper om meta tags te updaten of aan te maken
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Helper om link tags te updaten of aan te maken
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Helper om JSON-LD scripts te beheren
    const setJsonLd = (id: string, data: object) => {
      let script = document.getElementById(id) as HTMLScriptElement;
      
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // Set document title
    document.title = title;

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Language & region
    document.documentElement.lang = 'nl';
    setMetaTag('geo.region', 'NL');
    setMetaTag('geo.placename', 'Nederland');
    setMetaTag('content-language', 'nl');

    // Canonical
    setLinkTag('canonical', fullUrl);

    // Hreflang tags
    const setHreflangTag = (hreflang: string, href: string) => {
      let element = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.rel = 'alternate';
        element.setAttribute('hreflang', hreflang);
        document.head.appendChild(element);
      }
      element.href = href;
    };
    setHreflangTag('nl-NL', fullUrl);
    setHreflangTag('x-default', fullUrl);

    // Open Graph
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', fullUrl, true);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:locale', 'nl_NL', true);
    setMetaTag('og:site_name', SITE_NAME, true);

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:url', fullUrl);
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

    // Article-specific meta tags (for blog/informatie pages)
    if (ogType === 'article' && articleMeta) {
      if (articleMeta.publishedTime) {
        setMetaTag('article:published_time', articleMeta.publishedTime, true);
      }
      if (articleMeta.modifiedTime) {
        setMetaTag('article:modified_time', articleMeta.modifiedTime, true);
      }
      if (articleMeta.section) {
        setMetaTag('article:section', articleMeta.section, true);
      }
      setMetaTag('article:author', articleMeta.author || SITE_NAME, true);
    }

    // Extra SEO
    setMetaTag('author', SITE_NAME);
    setMetaTag('publisher', SITE_NAME);

    // JSON-LD: Organization
    setJsonLd('ld-organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: BASE_URL,
      logo: `${BASE_URL}/logo.png`,
      description: 'AI-powered platform voor WFT examenvoorbereiding in Nederland',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'NL'
      }
    });

    // JSON-LD: WebPage
    setJsonLd('ld-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: fullUrl,
      inLanguage: 'nl',
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: BASE_URL
      },
      ...(examType && {
        about: {
          '@type': 'Thing',
          name: `WFT ${examType} Examen`,
          description: `Officieel WFT examen voor ${examType} in Nederland`
        }
      }),
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2', '.speakable']
      }
    });

    // JSON-LD: Breadcrumb
    setJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: BASE_URL
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'WFT Examens',
          item: `${BASE_URL}/wft-examens`
        },
        ...(examType ? [{
          '@type': 'ListItem',
          position: 3,
          name: `WFT ${examType}`,
          item: fullUrl
        }] : [])
      ]
    });

    // JSON-LD: Course (indien courseInfo)
    if (courseInfo) {
      setJsonLd('ld-course', {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: courseInfo.name,
        description: courseInfo.description,
        provider: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: BASE_URL
        },
        educationalLevel: 'Professional',
        inLanguage: 'nl',
        coursePrerequisites: 'Geen voorkennis vereist',
        timeRequired: courseInfo.duration,
        offers: {
          '@type': 'Offer',
          price: courseInfo.price,
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          url: fullUrl
        },
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          courseWorkload: courseInfo.duration
        }
      });
    }

    // JSON-LD: FAQ (indien faqs)
    if (faqs.length > 0) {
      setJsonLd('ld-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      });
    }

    // Cleanup functie
    return () => {
      // We laten de meta tags staan voor SPA navigatie
      // Ze worden overschreven bij de volgende pagina
    };
  }, [config]);
};

export default useSEO;